import { ARMOR_SLOTS, itemDef, type ArmorSlot, type ItemDef, type ItemKind, type ToolKind, type WeaponFamily } from "@eldrun/content";
import { BELT_SLOTS, type SlotRef } from "@eldrun/sim";
import type { KitIcon } from "../kit/kit-model.ts";

/**
 * Partes puras do inventário em grade com detalhe (F82, mockup ui-c): o nome do slot, o tipo, a dica e os
 * atributos do item, os botões de ação (só os que o servidor já aceita: transferir, dividir, segurar e
 * largar) e a ocupação. O DOM (`inventory-panel.ts`) fica fino em volta disto; os testes rodam em node.
 */

export const KIND_NAMES: Record<ItemKind, string> = {
  material: "Material",
  comida: "Comida",
  ferramenta: "Ferramenta",
  arma: "Arma",
  armadura: "Armadura",
  municao: "Munição",
  construcao: "Construção",
  estacao: "Estação",
  utilitario: "Utilitário",
};

export const ARMOR_LABELS: Record<ArmorSlot, string> = { cabeca: "Cabeça", tronco: "Tronco", pernas: "Pernas", pes: "Pés" };
const TOOL_NAMES: Record<ToolKind, string> = { mao: "Mãos", machado: "Machado", picareta: "Picareta" };
const WEAPON_NAMES: Record<WeaponFamily, string> = { clava: "Clava", lanca: "Lança", lamina: "Lâmina", arco: "Arco", cajado: "Cajado" };
const SET_NAMES: Record<string, string> = { couro: "Couro", prata: "Prata", raio: "Pedra-de-raio" };
const EFFECT_NAMES: Record<string, string> = { regen: "Regeneração", saciedade: "Saciedade", folego: "Fôlego extra", defesa: "Defesa reforçada" };

/** Número com vírgula decimal (1,2), como o resto da UI em português. */
const num = (n: number): string => String(Math.round(n * 100) / 100).replace(".", ",");

/** Nome do slot (leitores de tela e o detalhe vazio): "Cinto 3", "Mochila 5", "Cabeça", "Aljava". */
export function slotName(ref: SlotRef): string {
  if (ref.area === "inv") return ref.index < BELT_SLOTS ? `Cinto ${ref.index + 1}` : `Mochila ${ref.index - BELT_SLOTS + 1}`;
  if (ref.area === "armor") return ARMOR_LABELS[ARMOR_SLOTS[ref.index] ?? "cabeca"];
  if (ref.area === "quiver") return "Aljava";
  return `Estação ${ref.index + 1}`;
}

/** Tipo do item com o subtipo: "Ferramenta · Machado", "Arma · Arco", "Armadura · Cabeça", "Comida · crua". */
export function itemType(def: ItemDef): string {
  const sub = def.tool ? TOOL_NAMES[def.tool.kind] : def.weapon ? WEAPON_NAMES[def.weapon.family] : def.armor ? ARMOR_LABELS[def.armor.slot] : def.food && (def.food.inedible || def.food.noRegen) ? "crua" : undefined;
  return sub ? `${KIND_NAMES[def.kind]} · ${sub}` : KIND_NAMES[def.kind];
}

/** O que fazer com o item (o Eldrun não tem descrições de sabor: a dica diz o uso). */
export function itemHint(def: ItemDef): string {
  switch (def.kind) {
    case "material":
      return def.fuel ? "Material de receitas; também queima no Forno e na Panela." : "Material de receitas.";
    case "comida":
      if (def.food?.inedible) return "Não se come cru: cozinhe na Panela.";
      if (def.food?.noRegen) return `Na mão, segure a ação principal para comer. Cru, corta a regeneração de Vida por ${def.food.noRegen} s.`;
      return "Na mão, segure a ação principal para comer.";
    case "ferramenta":
      return def.tool?.kind === "picareta" ? "Na mão, quebra pedras e minérios." : "Na mão, corta árvores.";
    case "arma":
      return def.weapon?.family === "arco" ? "Na mão, segure para puxar a corda; atira as flechas da Aljava." : "Na mão, golpeia com a ação principal.";
    case "armadura":
      return `Vista no slot de ${def.armor ? ARMOR_LABELS[def.armor.slot] : "armadura"} (Shift+clique).`;
    case "municao":
      return "Fica na Aljava; o arco atira dela.";
    case "construcao":
    case "estacao":
      return "Na mão, a mira vira posicionamento.";
    case "utilitario":
      return def.important ? "Item importante: não some no chão." : def.placeable ? "Na mão, a mira vira posicionamento." : "Utilitário.";
  }
}

export interface StatLine {
  icon?: KitIcon;
  label: string;
  value: string;
}

export interface ItemDetail {
  id: string;
  name: string;
  type: string;
  tier: number;
  badges: string[];
  hint: string;
  stats: StatLine[];
}

/** Atributos que o item tem de verdade (sem peso nem valor: o Eldrun não tem). */
export function itemStats(def: ItemDef, qty: number): StatLine[] {
  const rows: StatLine[] = [{ icon: "icone-pilha", label: "Pilha", value: `${qty} / ${def.stack}` }];
  const add = (label: string, value: string) => rows.push({ label, value });
  if (def.tool) {
    add("Coleta", `${def.tool.damage} · nível ${def.tool.tier}`);
    add("Golpe", `${num(def.tool.cooldown)} s`);
  }
  const w = def.weapon;
  if (w && w.family === "arco") {
    add("Flecha", `×${num(w.damage)}`);
    if (w.charge !== undefined) add("Carga", `${num(w.charge)} s`);
    if (w.arrows && w.arrows > 1) add("Flechas", String(w.arrows));
  } else if (w) {
    add("Dano", num(w.damage));
    add("Velocidade", `×${num(w.speed)}`);
    add("Alcance", `${num(w.reach)} m`);
    if (w.targets > 1) add("Alvos", String(w.targets));
  }
  if (w?.vsHaunt) add("Contra assombrações", `×${num(w.vsHaunt)}`);
  if (w?.vsBoss) add("Contra chefes", `×${num(w.vsBoss)}`);
  if (def.ammo) add("Dano", num(def.ammo.damage));
  if (def.armor) {
    add("Defesa", String(def.armor.defense));
    if (def.armor.set) add("Conjunto", SET_NAMES[def.armor.set] ?? def.armor.set);
  }
  if (def.food && !def.food.inedible) {
    add("Vida", `+${def.food.hp}`);
    add("Fome", `+${def.food.hunger}`);
    add("Fôlego", `+${def.food.stamina}`);
    if (def.food.effect) add("Efeito", `${EFFECT_NAMES[def.food.effect.id] ?? def.food.effect.id} · ${def.food.effect.seconds} s`);
  }
  if (def.fuel) add("Fogo", `${num(def.fuel.seconds)} s`);
  if (def.placeable) add("Resistência", String(def.placeable.hp));
  return rows;
}

/** Detalhe do item no slot (o painel da direita do ui-c); `undefined` para slot vazio ou item desconhecido. */
export function itemDetail(itemId: string, qty: number): ItemDetail | undefined {
  const def = itemId ? itemDef(itemId) : undefined;
  if (!def) return undefined;
  const badges = [def.stack > 1 ? "Empilhável" : "Único"];
  if (def.important) badges.push("Importante");
  return { id: def.id, name: def.name, type: itemType(def), tier: def.tier, badges, hint: itemHint(def), stats: itemStats(def, qty) };
}

export type SlotActionId = "transferir" | "dividir" | "segurar" | "largar";

export interface SlotActionView {
  id: SlotActionId;
  label: string;
  icon: KitIcon;
  enabled: boolean;
}

/** O que está aberto junto do inventário: nada, um Forno ou Panela, ou um baú. */
export type OpenStorage = "nenhum" | "estacao" | "bau";

/**
 * Rótulo da transferência rápida (o Shift+clique, `quickTransfer`), pelo que ela vai fazer com o item:
 * um verbo ou o destino, curto para caber no botão (o ⇄ do ícone diz "mover para").
 */
export function transferLabel(ref: SlotRef, itemId: string, storage: OpenStorage): string {
  if (ref.area === "armor") return "Despir";
  if (ref.area === "quiver") return "Tirar";
  if (ref.area === "estacao") return "Pegar";
  if (storage === "bau") return "Guardar";
  if (storage === "estacao") return "Estação";
  const def = itemDef(itemId);
  if (def?.armor) return "Vestir";
  if (def?.ammo) return "Aljava";
  return ref.index < BELT_SLOTS ? "Mochila" : "Cinto";
}

/**
 * Os quatro botões do detalhe (ui-c "Move, Split, Use, Drop"), cada um uma intenção que o servidor já
 * valida: transferir (`inv-rapido`), dividir (`inv-dividir`), segurar o item do cinto (`inv-selecionar`)
 * e largar a pilha (`inv-descartar`). Comer e atacar continuam na ação principal, com o item na mão.
 */
export function slotActions(ref: SlotRef, stack: { itemId: string; qty: number }, held: number, storage: OpenStorage): SlotActionView[] {
  const filled = stack.itemId !== "" && stack.qty > 0;
  const belt = ref.area === "inv" && ref.index < BELT_SLOTS;
  const inHand = belt && ref.index === held;
  return [
    { id: "transferir", label: filled ? transferLabel(ref, stack.itemId, storage) : "Mover", icon: "icone-mover", enabled: filled },
    { id: "dividir", label: "Dividir", icon: "icone-dividir", enabled: filled && stack.qty > 1 },
    { id: "segurar", label: inHand ? "Na mão" : "Segurar", icon: "icone-usar", enabled: filled && belt && !inHand },
    { id: "largar", label: "Largar", icon: "icone-largar", enabled: filled },
  ];
}

/** Ocupação do cinto e da mochila (o "Items 42 / 100" do mockup). */
export function capacity(slots: readonly { itemId: string; qty: number }[]): { used: number; total: number } {
  let used = 0;
  for (const s of slots) if (s.itemId && s.qty > 0) used++;
  return { used, total: slots.length };
}
